"use client";

import * as React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateNodesFromSerializedNodes, $insertGeneratedNodes } from "@lexical/clipboard";
import { $getRoot, $getSelection, COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from "lexical";

const LEXICAL_CLIPBOARD_MIME = "application/x-lexical-editor";

/**
 * `navigator.clipboard.write` 写入的 `application/x-lexical-editor` 在 `paste` 里
 * `clipboardData.getData` 往往为空；若此时 html/plain 也读不到，默认富文本粘贴会落空。
 * 在 Cmd+V 等手势下尝试 `navigator.clipboard.read()` 补读 JSON 并插入（失败则静默，与原先无异）。
 */
export function ClipboardAsyncReadPastePlugin() {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!editor.isEditable()) return false;
        if (!(event instanceof ClipboardEvent) || event.clipboardData == null) return false;
        const dt = event.clipboardData;
        if (dt.getData(LEXICAL_CLIPBOARD_MIME)) return false;

        const html = dt.getData("text/html").trim();
        const plain = dt.getData("text/plain").trim();
        if (html.length > 0 || plain.length > 0) return false;

        if (typeof navigator.clipboard?.read !== "function") return false;

        event.preventDefault();
        void (async () => {
          try {
            const items = await navigator.clipboard.read();
            for (const item of items) {
              if (!item.types.includes(LEXICAL_CLIPBOARD_MIME)) continue;
              const blob = await item.getType(LEXICAL_CLIPBOARD_MIME);
              const raw = await blob.text();
              let payload: { namespace?: string; nodes: unknown[] };
              try {
                payload = JSON.parse(raw) as { namespace?: string; nodes: unknown[] };
              } catch {
                continue;
              }
              if (payload.namespace !== editor._config.namespace || !Array.isArray(payload.nodes)) continue;

              editor.update(() => {
                let selection = $getSelection();
                if (selection === null) {
                  $getRoot().selectEnd();
                  selection = $getSelection();
                }
                if (selection === null) return;
                const nodes = $generateNodesFromSerializedNodes(payload.nodes as any);
                $insertGeneratedNodes(editor, nodes, selection);
              });
              return;
            }
          } catch {
            /* clipboard-read 权限或未支持 */
          }
        })();
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return null;
}
